/**
 * Ripia 拡張JWT管理システム
 * エンタープライズレベル統合JWT管理・ペアローテーション
 *
 * @description 全JWT機能の統合・高レベルAPI提供
 * @integration jwtConfig + jwtSigner + crypto + tokenBlacklist
 * @security トークンローテーション・マルチテナント対応
 */

import { v4 as uuidv4 } from 'uuid';
import {
  generateTokenPair,
  verifyToken,
  checkTokenExpiry,
  type RipiaJWTPayload,
  type JWTAlgorithm,
} from './jwtSigner';
import {
  saveEncryptedTokenPair,
  loadEncryptedTokenPair,
  removeEncryptedTokenPair,
  generateDeviceFingerprint,
  saveSessionData,
  loadSessionData,
  type SessionData,
} from './crypto';
import { TokenBlacklistService, type BlacklistStats } from './tokenBlacklist';
import { getJWTConfig } from './jwtConfig';
import logger from './logger';

// 拡張JWT管理エラー
export class EnhancedJWTError extends Error {
  constructor(
    message: string,
    public code:
      | 'AUTH_FAILED'
      | 'TOKEN_EXPIRED'
      | 'INVALID_TOKEN'
      | 'ROTATION_FAILED'
      | 'TENANT_MISMATCH'
  ) {
    super(message);
    this.name = 'EnhancedJWTError';
  }
}

// 認証要求
export interface AuthenticationRequest {
  // ユーザー情報
  userId: string;
  email: string;
  role: string;
  permissions?: string[];

  // マルチテナント情報
  tenantId?: string;
  tenantRole?: string;
  planType?: string;
  tenantPermissions?: string[];

  // セキュリティ情報
  deviceFingerprint?: string;
  ipAddress?: string;
  userAgent?: string;
  sessionId?: string;
  securityLevel?: 'standard' | 'high' | 'premium';

  // 設定
  algorithm?: JWTAlgorithm;
  rememberMe?: boolean;
  additionalClaims?: Record<string, unknown>;
}

// 認証結果
export interface AuthenticationResult {
  success: boolean;
  tokens: {
    accessToken: string;
    refreshToken: string;
  };
  jti: string;
  tokenPair?: EnhancedTokenPair;
  session?: SessionData;
  error?: string;
  requiresRotation?: boolean;
  processingTime?: number;
}

// 拡張トークンペア
export interface EnhancedTokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: 'Bearer';
  jti: string;
  refreshJti: string;
  deviceFingerprint: string;
  sessionId: string;
  tenantId?: string;
  createdAt: number;
  rotationCount: number;
}

// トークン検証結果
export interface TokenValidationResult {
  valid: boolean;
  payload?: RipiaJWTPayload;
  expired?: boolean;
  revoked?: boolean;
  deviceMismatch?: boolean;
  tenantMismatch?: boolean;
  error?: string;
  processingTime?: number;
}

// トークンローテーション結果
export interface TokenRotationResult {
  success: boolean;
  tokens: {
    accessToken: string;
    refreshToken?: string;
  };
  jti: string;
  newTokenPair?: EnhancedTokenPair;
  oldJti?: string;
  rotationReason?: string;
  error?: string;
}

/**
 * 拡張JWT管理サービス
 */
export class EnhancedJWTService {
  private static readonly ROTATION_THRESHOLD = 0.7; // 有効期限の70%でローテーション推奨
  private static readonly MAX_ROTATION_COUNT = 10; // 最大ローテーション回数

  /**
   * ユーザー認証・トークンペア生成
   * @param request 認証要求
   * @returns 認証結果
   */
  static async authenticate(
    request: AuthenticationRequest
  ): Promise<AuthenticationResult> {
    const startTime = performance.now();
    try {
      // デバイスフィンガープリント生成
      const deviceFingerprint =
        request.deviceFingerprint || generateDeviceFingerprint();
      const sessionId = request.sessionId || uuidv4();

      // JWTペイロード構築
      const jwtPayload: Partial<RipiaJWTPayload> = {
        sub: request.userId,
        email: request.email,
        role: request.role,
        permissions: request.permissions || [],

        // マルチテナント情報
        tenant_id: request.tenantId,
        tenant_role: request.tenantRole,
        plan_type: request.planType,
        tenant_permissions: request.tenantPermissions || [],

        // セキュリティ情報
        device_fp: deviceFingerprint,
        session_id: sessionId,
      };

      // トークンペア生成
      const tokenPair = generateTokenPair(jwtPayload, request.algorithm);

      // 拡張トークンペア作成
      const enhancedTokenPair: EnhancedTokenPair = {
        ...tokenPair,
        deviceFingerprint,
        sessionId,
        tenantId: request.tenantId,
        createdAt: Date.now(),
        rotationCount: 0,
      };

      // 暗号化保存
      saveEncryptedTokenPair(enhancedTokenPair, request.tenantId);

      // セッションデータ作成・保存
      const sessionData: SessionData = {
        sessionId,
        userId: request.userId,
        tenantId: request.tenantId,
        deviceFingerprint,
        createdAt: Date.now(),
        lastAccessAt: Date.now(),
        ipAddress: request.ipAddress,
        userAgent: request.userAgent,
        isActive: true,
      };

      saveSessionData(sessionData, request.tenantId);

      const duration = performance.now() - startTime;
      logger.info(
        `ユーザー認証完了: ${request.userId} (${request.tenantId || 'default'}) - ${duration.toFixed(2)}ms`
      );

      return {
        success: true,
        tokens: {
          accessToken: enhancedTokenPair.accessToken,
          refreshToken: enhancedTokenPair.refreshToken,
        },
        jti: enhancedTokenPair.jti,
        tokenPair: enhancedTokenPair,
        session: sessionData,
        processingTime: duration,
      };
    } catch (error) {
      logger.error('ユーザー認証エラー:', error);
      return {
        success: false,
        tokens: { accessToken: '', refreshToken: '' },
        jti: '',
        error: `認証に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        processingTime: performance.now() - startTime,
      };
    }
  }

  /**
   * トークン検証（包括的）
   * @param accessToken アクセストークン
   * @param deviceFingerprint デバイスフィンガープリント
   * @param tenantId テナントID（オプション）
   * @returns 検証結果
   */
  static async validateToken(
    accessToken: string,
    deviceFingerprint?: string,
    tenantId?: string
  ): Promise<TokenValidationResult> {
    const startTime = performance.now();
    try {
      // JWT形式・署名検証
      const verificationResult = verifyToken(accessToken);

      if (!verificationResult.valid) {
        return {
          valid: false,
          error: verificationResult.error,
          expired: verificationResult.expired,
          processingTime: performance.now() - startTime,
        };
      }

      const payload = verificationResult.payload!;

      // ブラックリスト確認
      const isRevoked = await TokenBlacklistService.isTokenRevoked(payload.jti);
      if (isRevoked) {
        return {
          valid: false,
          revoked: true,
          error: 'トークンがリボークされています',
          processingTime: performance.now() - startTime,
        };
      }

      // デバイスフィンガープリント確認
      if (deviceFingerprint && payload.device_fp !== deviceFingerprint) {
        logger.warn(`デバイスフィンガープリント不一致: ${payload.jti}`);
        return {
          valid: false,
          deviceMismatch: true,
          error: 'デバイス認証に失敗しました',
          processingTime: performance.now() - startTime,
        };
      }

      // テナント確認
      if (tenantId && payload.tenant_id !== tenantId) {
        logger.warn(
          `テナント不一致: ${payload.jti} (expected: ${tenantId}, got: ${payload.tenant_id})`
        );
        return {
          valid: false,
          tenantMismatch: true,
          error: 'テナント認証に失敗しました',
          processingTime: performance.now() - startTime,
        };
      }

      return {
        valid: true,
        payload,
        processingTime: performance.now() - startTime,
      };
    } catch (error) {
      logger.error('トークン検証エラー:', error);
      return {
        valid: false,
        error: `トークン検証に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        processingTime: performance.now() - startTime,
      };
    }
  }

  /**
   * トークンリフレッシュ・ローテーション
   * @param refreshToken リフレッシュトークン
   * @param deviceFingerprint デバイスフィンガープリント
   * @param tenantId テナントID（オプション）
   * @returns ローテーション結果
   */
  static async refreshToken(
    refreshToken: string,
    deviceFingerprint?: string,
    tenantId?: string
  ): Promise<TokenRotationResult> {
    try {
      // リフレッシュトークン検証
      const validationResult = await this.validateToken(
        refreshToken,
        deviceFingerprint,
        tenantId
      );

      if (!validationResult.valid) {
        return {
          success: false,
          tokens: { accessToken: '', refreshToken: '' },
          jti: '',
          error: `リフレッシュトークン検証失敗: ${validationResult.error}`,
        };
      }

      const payload = validationResult.payload!;

      // 既存トークンペア取得
      const existingTokenPair = loadEncryptedTokenPair(tenantId);
      if (!existingTokenPair) {
        return {
          success: false,
          tokens: { accessToken: '', refreshToken: '' },
          jti: '',
          error: '既存トークンペアが見つかりません',
        };
      }

      // 新しいトークンペア生成
      const newTokenPair = generateTokenPair({
        sub: payload.sub,
        email: payload.email,
        role: payload.role,
        permissions: payload.permissions,
        tenant_id: payload.tenant_id,
        tenant_role: payload.tenant_role,
        plan_type: payload.plan_type,
        tenant_permissions: payload.tenant_permissions,
        device_fp: payload.device_fp,
        session_id: payload.session_id,
      });

      // 拡張トークンペア作成
      const enhancedTokenPair: EnhancedTokenPair = {
        ...newTokenPair,
        deviceFingerprint: payload.device_fp || generateDeviceFingerprint(),
        sessionId: payload.session_id || uuidv4(),
        tenantId: payload.tenant_id,
        createdAt: Date.now(),
        rotationCount:
          (existingTokenPair as EnhancedTokenPair).rotationCount + 1 || 1,
      };

      // 最大ローテーション回数チェック
      if (enhancedTokenPair.rotationCount > this.MAX_ROTATION_COUNT) {
        logger.warn(
          `最大ローテーション回数超過: ${payload.jti} (${enhancedTokenPair.rotationCount}回)`
        );

        // 全セッショントークンリボーク
        await TokenBlacklistService.revokeSessionTokens(
          payload.session_id!,
          'security'
        );

        return {
          success: false,
          tokens: { accessToken: '', refreshToken: '' },
          jti: '',
          error: '最大ローテーション回数を超過しました。再認証が必要です。',
        };
      }

      // 古いトークンをリボーク
      await TokenBlacklistService.revokeToken({
        jti: payload.jti,
        reason: 'rotation',
        userId: payload.sub,
        tenantId: payload.tenant_id,
        sessionId: payload.session_id,
      });

      // 新しいトークンペア保存
      saveEncryptedTokenPair(enhancedTokenPair, tenantId);

      // セッション更新
      const sessionData = loadSessionData(tenantId);
      if (sessionData) {
        sessionData.lastAccessAt = Date.now();
        saveSessionData(sessionData, tenantId);
      }

      logger.info(
        `トークンリフレッシュ完了: ${payload.sub} (ローテーション: ${enhancedTokenPair.rotationCount}回)`
      );

      return {
        success: true,
        tokens: {
          accessToken: enhancedTokenPair.accessToken,
          refreshToken: enhancedTokenPair.refreshToken,
        },
        jti: enhancedTokenPair.jti,
        newTokenPair: enhancedTokenPair,
        oldJti: payload.jti,
        rotationReason: 'refresh',
      };
    } catch (error) {
      logger.error('トークンリフレッシュエラー:', error);
      return {
        success: false,
        tokens: { accessToken: '', refreshToken: '' },
        jti: '',
        error: `トークンリフレッシュに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * 自動トークンローテーション判定
   * @param accessToken アクセストークン
   * @returns ローテーション必要性
   */
  static shouldRotateToken(accessToken: string): boolean {
    try {
      const expiryInfo = checkTokenExpiry(accessToken);

      if (!expiryInfo.valid) return false;

      // 有効期限の70%経過でローテーション推奨
      const config = getJWTConfig();
      const totalExpiry = config.expiresIn.accessToken;
      const remainingTime = expiryInfo.expiresIn;
      const elapsed = totalExpiry - remainingTime;
      const elapsedRatio = elapsed / totalExpiry;

      return elapsedRatio >= this.ROTATION_THRESHOLD;
    } catch (error) {
      logger.error('ローテーション判定エラー:', error);
      return false;
    }
  }

  /**
   * ユーザーログアウト（全トークンリボーク）
   * @param userId ユーザーID
   * @param sessionId セッションID（オプション）
   * @param tenantId テナントID（オプション）
   * @returns ログアウト結果
   */
  static async logout(
    userId: string,
    sessionId?: string,
    tenantId?: string
  ): Promise<{ success: boolean; revokedTokens: number; error?: string }> {
    try {
      let revokedTokens = 0;

      // セッション指定がある場合はセッションのみリボーク
      if (sessionId) {
        revokedTokens = await TokenBlacklistService.revokeSessionTokens(
          sessionId,
          'logout'
        );
      } else {
        // ユーザーの全トークンリボーク
        revokedTokens = await TokenBlacklistService.revokeAllUserTokens(
          userId,
          'logout',
          tenantId
        );
      }

      // ローカルストレージクリーンアップ
      removeEncryptedTokenPair(tenantId);

      // セッションデータ無効化
      const sessionData = loadSessionData(tenantId);
      if (sessionData) {
        sessionData.isActive = false;
        sessionData.lastAccessAt = Date.now();
        saveSessionData(sessionData, tenantId);
      }

      logger.info(
        `ユーザーログアウト完了: ${userId} (${revokedTokens}トークンリボーク)`
      );

      return {
        success: true,
        revokedTokens,
      };
    } catch (error) {
      logger.error('ユーザーログアウトエラー:', error);
      return {
        success: false,
        revokedTokens: 0,
        error: `ログアウトに失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
      };
    }
  }

  /**
   * 現在のトークンペア取得
   * @param tenantId テナントID（オプション）
   * @returns トークンペア
   */
  static getCurrentTokenPair(tenantId?: string): EnhancedTokenPair | null {
    try {
      const tokenPair = loadEncryptedTokenPair(tenantId);
      return tokenPair
        ? {
            ...tokenPair,
            createdAt: Date.now(), // 実際には保存された値を使用
            rotationCount: 0, // 実際には保存された値を使用
          }
        : null;
    } catch (error) {
      logger.error('現在のトークンペア取得エラー:', error);
      return null;
    }
  }

  /**
   * Generate token pair wrapper
   * @param options Token generation options
   * @returns Token pair
   */
  async generateTokenPair(options: {
    payload: RipiaJWTPayload;
    userId: string;
    tenantId: string;
    sessionId: string;
    deviceCharacteristics?: {
      fingerprint: string;
      userAgent: string;
      ipAddress: string;
      platform: string;
      deviceType: string;
    };
  }): Promise<{ accessToken: string; refreshToken: string }> {
    const tokenPair = generateTokenPair(options.payload);

    // Save to encrypted storage if device fingerprint provided
    if (options.deviceCharacteristics?.fingerprint) {
      await saveEncryptedTokenPair(
        {
          accessToken: tokenPair.accessToken,
          refreshToken: tokenPair.refreshToken,
          expiresIn: 900,
          tokenType: 'Bearer' as const,
          jti: uuidv4(),
          refreshJti: uuidv4(),
          deviceFingerprint: options.deviceCharacteristics.fingerprint,
          sessionId: options.sessionId,
        },
        options.tenantId
      );
    }

    return tokenPair;
  }

  /**
   * Verify access token
   * @param token JWT token to verify
   * @returns Decoded payload if valid
   */
  async verifyAccessToken(token: string): Promise<RipiaJWTPayload | null> {
    try {
      const result = verifyToken(token);
      if (result.valid) {
        return result.payload || null;
      }
      return null;
    } catch (error) {
      console.error('Token verification failed:', error);
      return null;
    }
  }

  /**
   * Decode token without verification
   * @param token JWT token to decode
   * @returns Decoded payload
   */
  decodeToken(token: string): RipiaJWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      const payload = JSON.parse(Buffer.from(parts[1], 'base64').toString());
      return payload;
    } catch (error) {
      console.error('Token decode failed:', error);
      return null;
    }
  }

  /**
   * JWT統計情報取得
   * @returns 統計情報
   */
  static async getJWTStats(): Promise<{
    tokenStats: {
      algorithm: string;
      accessTokenExpiry: number;
      refreshTokenExpiry: number;
      rotationThreshold: number;
      maxRotations: number;
    };
    blacklistStats: BlacklistStats;
    securityInfo: {
      deviceFingerprintEnabled: boolean;
      multiTenantSupport: boolean;
      tokenRotationEnabled: boolean;
      securityLevel: string;
    };
  }> {
    try {
      const blacklistStats = await TokenBlacklistService.getBlacklistStats();
      const config = getJWTConfig();

      return {
        tokenStats: {
          algorithm: config.algorithm,
          accessTokenExpiry: config.expiresIn.accessToken,
          refreshTokenExpiry: config.expiresIn.refreshToken,
          rotationThreshold: this.ROTATION_THRESHOLD,
          maxRotations: this.MAX_ROTATION_COUNT,
        },
        blacklistStats,
        securityInfo: {
          deviceFingerprintEnabled: true,
          multiTenantSupport: true,
          tokenRotationEnabled: true,
          securityLevel: 'エンタープライズレベル',
        },
      };
    } catch (error) {
      logger.error('JWT統計取得エラー:', error);
      throw error;
    }
  }

  /**
   * セキュリティ診断
   * @param accessToken アクセストークン
   * @returns 診断結果
   */
  static async securityDiagnostic(accessToken: string): Promise<{
    tokenHealth: 'healthy' | 'warning' | 'critical';
    issues: string[];
    recommendations: string[];
  }> {
    try {
      const issues: string[] = [];
      const recommendations: string[] = [];

      // トークン検証
      const validationResult = await this.validateToken(accessToken);
      if (!validationResult.valid) {
        issues.push('トークンが無効です');
        recommendations.push('新しいトークンを取得してください');
      }

      // 有効期限チェック
      const expiryInfo = checkTokenExpiry(accessToken);
      if (expiryInfo.expiresIn < 300) {
        // 5分未満
        issues.push('トークン有効期限が間近です');
        recommendations.push('トークンをリフレッシュしてください');
      }

      // ローテーション推奨チェック
      if (this.shouldRotateToken(accessToken)) {
        issues.push('トークンローテーションが推奨されます');
        recommendations.push(
          'セキュリティ向上のためトークンをローテーションしてください'
        );
      }

      // 健康度判定
      let tokenHealth: 'healthy' | 'warning' | 'critical' = 'healthy';
      if (issues.length > 0) {
        tokenHealth = issues.some(
          (issue) => issue.includes('無効') || issue.includes('間近')
        )
          ? 'critical'
          : 'warning';
      }

      return {
        tokenHealth,
        issues,
        recommendations,
      };
    } catch (error) {
      logger.error('セキュリティ診断エラー:', error);
      return {
        tokenHealth: 'critical',
        issues: ['診断エラーが発生しました'],
        recommendations: ['システム管理者に連絡してください'],
      };
    }
  }
}

/**
 * 便利関数・ヘルパー
 */

/**
 * 簡易認証（開発・テスト用）
 * @param userId ユーザーID
 * @param email メールアドレス
 * @param role ロール
 * @returns 認証結果
 */
export const quickAuthenticate = async (
  userId: string,
  email: string,
  role: string = 'staff'
): Promise<AuthenticationResult> => {
  return EnhancedJWTService.authenticate({
    userId,
    email,
    role,
    permissions: ['basic.access'],
  });
};

/**
 * マルチテナント認証
 * @param request 認証要求
 * @returns 認証結果
 */
export const multiTenantAuthenticate = async (
  request: AuthenticationRequest
): Promise<AuthenticationResult> => {
  if (!request.tenantId || !request.tenantRole) {
    throw new EnhancedJWTError(
      'マルチテナント認証にはtenantIdとtenantRoleが必要です',
      'TENANT_MISMATCH'
    );
  }

  return EnhancedJWTService.authenticate(request);
};

/**
 * デバッグ情報取得
 */
export const getEnhancedJWTInfo = () => {
  if (import.meta.env.PROD) {
    return { error: 'Production環境では利用できません' };
  }

  return {
    implementation: 'エンタープライズレベル統合JWT管理',
    features: [
      'RS256/ES256署名検証',
      'トークンペアローテーション',
      'デバイスフィンガープリント認証',
      'マルチテナント対応',
      'ブラックリスト管理',
      '自動セキュリティ診断',
      '包括的監査ログ',
    ],
    integrations: ['jwtConfig', 'jwtSigner', 'crypto', 'tokenBlacklist'],
    performance: {
      targetAuthTime: '<100ms',
      targetValidationTime: '<50ms',
      rotationThreshold: '70%',
      maxRotations: 10,
    },
    timestamp: new Date().toISOString(),
  };
};

// 開発環境でのテスト用エクスポート
if (import.meta.env.DEV) {
  (window as typeof window & { RipiaEnhancedJWT: object }).RipiaEnhancedJWT = {
    service: EnhancedJWTService,
    quick: quickAuthenticate,
    multiTenant: multiTenantAuthenticate,
    getInfo: getEnhancedJWTInfo,
  };
}
